import { useEffect, useRef } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Button, Stack, Typography } from '@lyearn/ui';

import { questionButtonStyles } from '../../styles';

const FollowUp = ({
  onClickFollowUp,
  followUpQuestions,
  scrollDown,
  atBottomRef,
}: {
  onClickFollowUp?: (value: string) => void;
  followUpQuestions?: string[];
  scrollDown: () => void;
  atBottomRef: React.MutableRefObject<boolean>;
}) => {
  const { t } = useTranslation();

  const bottomRef = useRef<HTMLDivElement>(null);
  const hasQuestions = Boolean(followUpQuestions?.length);

  // Show the questions as soon as they arrive (no artificial delay) and follow the
  // scroll only if the user is already pinned to the bottom.
  useEffect(() => {
    if (hasQuestions) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      if (atBottomRef.current) {
        scrollDown();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followUpQuestions]);

  if (!hasQuestions) {
    return null;
  }

  const handleClick = (question: string) => {
    onClickFollowUp?.(question);
    scrollDown();
  };

  return (
    <Stack className="mr-12 ml-[4.5rem] max-w-[32.8rem]" direction="col">
      <Stack className="-mt-12" direction="col" gapY={8}>
        <Typography className="text-text-secondary mb-8 pl-4" variant="label-02">
          {t('Common:KnowledgeBase.FollowUpQuestions')}
        </Typography>
        <Stack className="flex-wrap" direction="row" gap={12}>
          {followUpQuestions?.map((question, index) => (
            <Button
              key={index}
              fullWidth
              alignment="left"
              appearance="secondary"
              className={cn('!p-[1.1rem] h-auto w-fit border-none', questionButtonStyles)}
              shape="square"
              variant="outlined"
              onClick={() => handleClick(question)}>
              <Typography className={cn('text-left')} variant="body-short-03">
                {question}
              </Typography>
            </Button>
          ))}
        </Stack>
        <div ref={bottomRef} />
      </Stack>
    </Stack>
  );
};

export default FollowUp;
